/**
 * BroadcastSession — the "per login" scope of the announcements pop-up engine.
 *
 * The server decides which pop-ups are due per login; "login" on the client is the life of the
 * browser tab, exactly the scope the old referral banner used (sessionStorage). The key is sent
 * with every /broadcasts/due call and every /broadcasts/{id}/events post, so re-show rules such
 * as next_login and the max-per-login cap are evaluated against one stable id per tab.
 *
 * Reset on logout and on login success so the next user in the same tab starts fresh.
 */
const KEY = "evox_broadcast_session";
const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
const LENGTH = 24; // the server accepts /^[A-Za-z0-9]{16,32}$/

const mint = () => {
    let out = "";
    const cryptoObj = typeof window !== "undefined" ? (window.crypto || window.msCrypto) : null;
    if (cryptoObj && cryptoObj.getRandomValues) {
        const bytes = new Uint8Array(LENGTH);
        cryptoObj.getRandomValues(bytes);
        for (let i = 0; i < LENGTH; i++) {
            out += ALPHABET[bytes[i] % ALPHABET.length];
        }
    } else {
        for (let i = 0; i < LENGTH; i++) {
            out += ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
        }
    }
    return out;
};

// The ids whose `shown` event this tab already posted, tied to the key they were posted under:
// {key, ids}. The Dashboard (and so BroadcastQueue) can remount within one login session; without
// this memory every return to the Dashboard would post a fresh `shown` for the same item.
const SHOWN_KEY = "evox_broadcast_shown";

const readShown = (key) => {
    try {
        const parsed = JSON.parse(sessionStorage.getItem(SHOWN_KEY) || "null");
        return parsed && parsed.key === key && Array.isArray(parsed.ids) ? parsed.ids : [];
    } catch (e) {
        return [];
    }
};

const BroadcastSession = {
    get() {
        let key = sessionStorage.getItem(KEY);
        if (!key) {
            key = mint();
            sessionStorage.setItem(KEY, key);
        }
        return key;
    },
    wasShown(id) {
        return readShown(this.get()).includes(Number(id));
    },
    markShown(id) {
        const key = this.get();
        const ids = readShown(key);
        if (!ids.includes(Number(id))) {
            ids.push(Number(id));
        }
        try {
            sessionStorage.setItem(SHOWN_KEY, JSON.stringify({ key: key, ids: ids }));
        } catch (e) {
            // quota / private mode: the in-memory guard in BroadcastQueue still covers this mount
        }
    },
    reset() {
        sessionStorage.removeItem(KEY);
        sessionStorage.removeItem(SHOWN_KEY);
    },
};

export default BroadcastSession;
